/** Player stories for the current men’s first team, with reference links into the Wiki. */
import { useEffect, useState } from 'react';
import { DetailHeader, EmptyNote, Rule, StaleBadge, YearTag } from '../components/chrome';
import { ShareButton } from '../components/ShareButton';
import { fetchTakes } from '../lib/api';
import { FEEDS, useFeed } from '../lib/feeds';
import { honourEntryId } from '../lib/codex';
import { playerLink } from '../lib/share';
import type { ClubFeed, CodexFeed, Player, Take } from '../lib/types';


// ── Player profile — an editorial spread, not a card. Type carries it; no
//    no gamification. The number is a quiet watermark, the name leads.
const PlayerHeader = ({ player }: { player: Player }) => (
  <header className="player-head">
    {player.squadNumberVerified !== false && <span className="player-head-num" aria-hidden="true">{player.num}</span>}
    {player.story?.terraceName && <span className="player-kicker">“{player.story.terraceName}”</span>}
    <h2 className="player-title">{player.full}</h2>
    <p className="player-line">
      <span>{player.pos}</span>
      <span aria-hidden="true">·</span>
      <span>{player.squadNumberVerified === false ? 'Squad number TBC' : `No. ${player.num}`}</span>
      <span aria-hidden="true">·</span>
      <span>{player.nat}</span>
    </p>
  </header>
);

/** A recent arrival with no verified Arsenal stat line yet shows a calm
 *  "new to Arsenal" line in place of fabricated figures — never a "0 goals". */
const ArrivalLine = ({ arrival }: { arrival: NonNullable<Player['arrival']> }) => (
  <p className="player-loan-note">
    {arrival.loan
      ? `On loan from ${arrival.from}`
      : /academy|hale end/i.test(arrival.from)
        ? `${arrival.from === 'Hale End' ? 'Hale End' : 'Academy'} pathway`
      : `New to Arsenal — from ${arrival.from}${arrival.year && arrival.year !== '—' ? `, ${arrival.year}` : ''}`}
  </p>
);

const playerKey = (value: string): string =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function verifiedLeagueGoals(club: ClubFeed, player: Player): number | null {
  const scorer = club.lastSeason?.scorers.goals.find((s) => playerKey(s.n) === playerKey(player.name));
  return scorer?.v ?? null;
}

/** A compact verified line sourced from the club scorer table. If the central
 *  table has no figure for this player, the profile stays editorial. */
const SeasonLine = ({ player, club }: { player: Player; club: ClubFeed }) => {
  const goals = verifiedLeagueGoals(club, player);
  if (player.arrival) {
    return (
      <>
        <ArrivalLine arrival={player.arrival} />
        {goals !== null && (
          <div className="season-figures season-figures--verified">
            <div>
              <strong>{goals}</strong>
              <em>League goals</em>
            </div>
          </div>
        )}
      </>
    );
  }
  if (goals === null) return null;
  return (
    <div className="season-figures season-figures--verified">
      <div>
        <strong>{goals}</strong>
        <em>League goals</em>
      </div>
    </div>
  );
};

// ── Player detail — the programme profile: fact meets the terrace ───────────

/** Takes that mention this player — the fans' running commentary on him. */
const TerraceVerdict = ({ player, onOpenTerrace }: { player: Player; onOpenTerrace?: () => void }) => {
  const [mentions, setMentions] = useState<Take[]>([]);
  useEffect(() => {
    let live = true;
    fetchTakes().then(({ takes }) => {
      if (!live) return;
      const needles = [player.name, player.full, player.story?.terraceName]
        .filter((n): n is string => Boolean(n))
        .map((n) => n.toLowerCase());
      setMentions(
        takes
          .filter((t) => {
            const text = t.text.toLowerCase();
            return needles.some((n) => text.includes(n));
          })
          .sort((a, b) => b.up - a.up)
          .slice(0, 2),
      );
    });
    return () => {
      live = false;
    };
  }, [player.id]);

  if (mentions.length === 0) return null;
  return (
    <>
      <Rule label="The North Bank says" accent="#EF0107" />
      <ul className="verdicts">
        {mentions.map((t) => (
          <li key={t.id} className="verdict">
            <p>“{t.text}”</p>
            <footer>
              <span className="verdict-handle">{t.handle}</span>
              <span className="verdict-votes">▲ {t.up.toLocaleString()}</span>
            </footer>
          </li>
        ))}
      </ul>
      {onOpenTerrace && (
        <div className="verdict-cta">
          <button className="ghost-btn" onClick={onOpenTerrace}>
            Add yours on the North Bank
          </button>
        </div>
      )}
    </>
  );
};

const PlayerDetail = ({
  player,
  onBack,
  backLabel,
  trail,
  onOpenTerrace,
  codex,
  club,
  onOpenEntry,
  onOpenYear,
}: {
  player: Player;
  onBack: () => void;
  /** Names where Back returns to (the layer/tab beneath this one). */
  backLabel?: string;
  /** The full ancestor path for the "where am I" breadcrumb. */
  trail?: string[];
  onOpenTerrace?: () => void;
  codex: CodexFeed;
  club: ClubFeed;
  onOpenEntry?: (id: string) => void;
  onOpenYear?: (year: string) => void;
  /** Legacy query compatibility; historical references are now served by Wiki. */
  onOpenLore?: (matchId: string) => void;
  startInClub?: boolean;
}) => {
  const story = player.story;
  // A recent arrival has no verified stat line — the share line says where he
  // came from rather than presenting a fabricated stat.
  const goals = verifiedLeagueGoals(club, player);
  const arrivalShare =
    player.arrival
      ? player.arrival.loan
        ? `on loan from ${player.arrival.from}`
        : `new to Arsenal — from ${player.arrival.from}${player.arrival.year && player.arrival.year !== '—' ? `, ${player.arrival.year}` : ''}`
      : null;
  const shareText = arrivalShare
    ? `${player.full} (#${player.num}) — ${arrivalShare}. ${story?.lede ?? player.note ?? ''}`.trim()
      : goals !== null
        ? `${player.full} (#${player.num}) — ${goals} league goals in the checked title table. ${story?.lede ?? player.note ?? ''}`.trim()
        : `${player.full} (#${player.num}). ${story?.lede ?? player.note ?? ''}`.trim();
  return (
    <div className="screen detail-screen player-screen">
      <DetailHeader
        onBack={onBack}
        backLabel={backLabel ?? 'Club'}
        trail={trail}
        right={
          <ShareButton
            ghost
            label="Share"
            moment={{
              title: `COYG — ${player.full}`,
              text: shareText,
              url: playerLink(player.id),
              card: {
                eyebrow: `${player.pos} · NO. ${player.num}`,
                headline: player.full,
                detail: story?.lede ?? player.note ?? 'Arsenal men’s first team',
                stamp: 'THE CLUB · COYG.ORG',
              },
            }}
          />
        }
      />

      <PlayerHeader player={player} />
      {story && <p className="player-lede">{story.lede}</p>}

      <SeasonLine player={player} club={club} />

      {/* The story — the wiki-style history, read as one uninterrupted block. */}
      {story ? (
        <>
          {story.chapters.map((ch) => (
            <article key={ch.title} className="story-chapter">
              <Rule label={ch.title} accent="var(--gold)" />
              <p className="story-text">{ch.text}</p>
            </article>
          ))}

          {story.timeline.length > 0 && (
            <>
              <Rule label="Timeline" />
              <ol className="story-timeline">
                {story.timeline.map((t, i) => (
                  <li key={`${t.year}-${i}`}>
                    <YearTag year={t.year} onOpenYear={onOpenYear} />
                    <span className="story-event">{t.event}</span>
                  </li>
                ))}
              </ol>
            </>
          )}

          <div className="fact-file">
            <div>
              <em>Born</em>
              <strong>{story.factFile.born}</strong>
            </div>
            <div>
              <em>Signed</em>
              <strong>{story.factFile.signed}</strong>
            </div>
            {story.honours.length > 0 && (
              <div className="fact-honours">
                <em>Honours</em>
                <div className="honour-chips">
                  {story.honours.map((h) => {
                    const entryId = honourEntryId(h, codex);
                    return entryId && onOpenEntry ? (
                      <button key={h} className="honour-chip honour-chip--link" onClick={() => onOpenEntry(entryId)}>
                        {h} <span aria-hidden="true">→</span>
                      </button>
                    ) : (
                      <span key={h} className="honour-chip">
                        {h}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <a
            className="wiki-link"
            href={`https://en.wikipedia.org/wiki/Special:Search/${encodeURIComponent(player.full)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Read more on Wikipedia →
          </a>
        </>
      ) : (
        player.note && (
          <>
            <Rule label="The book on him" accent="var(--gold)" />
            <blockquote className="oracle">
              <p>{player.note}</p>
            </blockquote>
          </>
        )
      )}

      {/* The fans' verdict — community last, facts first. */}
      <TerraceVerdict player={player} onOpenTerrace={onOpenTerrace} />
    </div>
  );
};

const SQUAD_REFERENCE_LINKS: Array<{ href: string; label: string; detail: string }> = [
  { href: '/wiki/players', label: 'Player reference', detail: 'Profiles, roles and squad notes' },
  { href: '/wiki/analysis', label: 'Analysis', detail: 'Historical match evidence and methods' },
  { href: '/wiki/trophies', label: 'Honours', detail: 'Trophies and seasons in the archive' },
  { href: '/wiki/history', label: 'History', detail: 'Seasons, legends and club stories' },
  { href: '/wiki/glossary', label: 'Glossary', detail: 'Club language and places' },
];

// ── Screen ───────────────────────────────────────────────────────────────────
export const SquadScreen = ({
  openPlayerId,
  onOpenPlayer,
  onClosePlayer,
  onOpenTerrace,
  onOpenEntry,
  onOpenYear,
  onOpenLore: _onOpenLore,
  startInClub: _startInClub,
  backLabel,
  trail,
}: {
  openPlayerId: string | null;
  onOpenPlayer: (id: string) => void;
  onClosePlayer: () => void;
  onOpenTerrace?: () => void;
  onOpenEntry?: (id: string) => void;
  onOpenYear?: (year: string) => void;
  /** Legacy query compatibility; historical references are now served by Wiki. */
  onOpenLore?: (matchId: string) => void;
  startInClub?: boolean;
  backLabel?: string;
  trail?: string[];
}) => {
  const squad = useFeed(FEEDS.squad);
  const club = useFeed(FEEDS.club);
  const codex = useFeed(FEEDS.codex);
  const currentPlayers = squad.data.players.filter((player) => player.current !== false);
  const open = openPlayerId ? currentPlayers.find((p) => p.id === openPlayerId) ?? null : null;
  if (open)
    return (
      <PlayerDetail
        player={open}
        onBack={onClosePlayer}
        backLabel={backLabel}
        trail={trail}
        onOpenTerrace={onOpenTerrace}
        codex={codex.data}
        club={club.data}
        onOpenEntry={onOpenEntry}
        onOpenYear={onOpenYear}
      />
    );

  const groups = [...new Set(currentPlayers.map((p) => p.group))];

  return (
    <div className="screen squad-screen">
      <div className="screen-head">
        <div>
          <h1 className="screen-title">Squad</h1>
          <p className="screen-sub">
            {`Player stories · men’s first-team snapshot · checked ${new Intl.DateTimeFormat('en-GB', { dateStyle: 'long', timeZone: 'Europe/London' }).format(new Date(squad.data.checkedAt))}`}
          </p>
        </div>
        <div className="screen-head-right">
          <nav className="squad-reference-nav" aria-label="Squad references">
            {SQUAD_REFERENCE_LINKS.map((reference) => <a key={reference.href} href={reference.href}><strong>{reference.label}</strong><span>{reference.detail}</span></a>)}
          </nav>
          <StaleBadge stale={squad.stale} asOf={squad.data.checkedAt} />
        </div>
      </div>

      {groups.map((g) => (
            <div key={g}>
              <Rule label={g} accent="var(--gold)" />
              <div className="player-grid">
                {currentPlayers
                  .filter((p) => p.group === g)
                  .map((p) => (
                    <button key={p.id} className="player-card" onClick={() => onOpenPlayer(p.id)}>
                      <span className="player-card-num">{p.squadNumberVerified === false ? '—' : p.num}</span>
                      <span className="player-card-name">{p.name}</span>
                      <span className="player-card-sub">
                        {p.pos} · {p.nat}
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          ))}
          {currentPlayers.length === 0 && <EmptyNote>Player stories arriving soon.</EmptyNote>}

    </div>
  );
};
